import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { UiStateService } from '../../services/ui-state.service'

// Constants for access method values - use these to avoid typos
export const ACCESS_METHOD = {
  CLOUDFORMATION: 'cloudformation' as const,
  STACKSETS: 'stacksets' as const,
}

export type AccessMethodType = 'cloudformation' | 'stacksets'

/**
 * Service to track migration intent for access method changes.
 *
 * This service stores the user's saved intent for switching between CloudFormation
 * and StackSets access methods. It uses the UIState API to persist intent at the
 * account level, keyed by sync pair (source/target endpoint URIs).
 *
 * Key concepts:
 * - Migration intent is per sync pair, not per application (since templates are shared)
 * - Intent is only saved when the user explicitly saves the form, not on radio button selection
 * - Intent is automatically cleared when actual state matches intent (migration complete)
 *
 * Usage:
 * 1. Call setAccountId() when account is loaded
 * 2. Use getMigrationIntent() to check if there's a pending migration for a sync pair
 * 3. Call setMigrationIntent() when user saves with a different access method
 * 4. Call clearMigrationIntent() when migration is complete (actual matches intent)
 */
@Injectable({
  providedIn: 'root',
})
export class MigrationIntentService {
  private accountId: string = null
  private state$ = new BehaviorSubject<Record<string, string>>({})
  private saveInProgress = false
  private pendingSave = false
  private loadSubscription: Subscription = null

  constructor(private uiStateService: UiStateService) {}

  /**
   * Set the current account ID. Must be called before using other methods.
   * Automatically loads state from backend when account changes.
   */
  setAccountId(accountId: string): void {
    if (this.accountId !== accountId) {
      this.accountId = accountId
      this.loadStateFromBackend()
    }
  }

  /**
   * Get the migration intent for a sync pair.
   * Returns the target access method the user wants to migrate to,
   * or null if no migration is in progress.
   */
  getMigrationIntent(sourceEndpoint: string, targetEndpoint: string): AccessMethodType | null {
    const key = this.buildKey(sourceEndpoint, targetEndpoint)
    const value = this.state$.value[key]
    if (value === 'stacksets' || value === 'cloudformation') {
      return value as AccessMethodType
    }
    return null
  }

  /**
   * Observable version of getMigrationIntent for reactive usage.
   */
  getMigrationIntent$(sourceEndpoint: string, targetEndpoint: string): Observable<AccessMethodType | null> {
    return new Observable((subscriber) => {
      const subscription = this.state$.subscribe((state) => {
        const key = this.buildKey(sourceEndpoint, targetEndpoint)
        const value = state[key]
        if (value === 'stacksets' || value === 'cloudformation') {
          subscriber.next(value as AccessMethodType)
        } else {
          subscriber.next(null)
        }
      })
      return () => subscription.unsubscribe()
    })
  }

  /**
   * Set migration intent for a sync pair.
   * Called when user saves the form with a different access method selected.
   */
  setMigrationIntent(sourceEndpoint: string, targetEndpoint: string, intent: AccessMethodType): void {
    const key = this.buildKey(sourceEndpoint, targetEndpoint)
    const newState = { ...this.state$.value, [key]: intent }
    this.state$.next(newState)
    this.saveState()
  }

  /**
   * Clear migration intent for a sync pair.
   * Called when migration is complete (actual state matches intent) or cancelled.
   */
  clearMigrationIntent(sourceEndpoint: string, targetEndpoint: string): void {
    const key = this.buildKey(sourceEndpoint, targetEndpoint)
    if (!(key in this.state$.value)) {
      return // Nothing to clear
    }
    const newState = { ...this.state$.value }
    delete newState[key]
    this.state$.next(newState)
    this.saveState()
  }

  /**
   * Check if there is a migration intent for a sync pair.
   */
  hasMigrationIntent(sourceEndpoint: string, targetEndpoint: string): boolean {
    return this.getMigrationIntent(sourceEndpoint, targetEndpoint) !== null
  }

  /**
   * Build the UIState key for a sync pair.
   * Format: 'migration.accessMethod.{sourceEndpoint}.{targetEndpoint}'
   */
  private buildKey(sourceEndpoint: string, targetEndpoint: string): string {
    return `migration.accessMethod.${sourceEndpoint}.${targetEndpoint}`
  }

  /**
   * Load all migration-related UIState from backend.
   */
  private loadStateFromBackend(): void {
    if (!this.accountId) return

    // Cancel any in-progress load
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe()
    }

    this.loadSubscription = this.uiStateService.load(this.accountId).subscribe({
      next: (state) => {
        // Filter to only include migration.accessMethod keys
        const migrationState: Record<string, string> = {}
        if (state) {
          for (const key of Object.keys(state)) {
            if (key.startsWith('migration.accessMethod.')) {
              migrationState[key] = state[key]
            }
          }
        }
        this.state$.next(migrationState)
        this.loadSubscription = null
      },
      error: () => {
        // On error, just use empty state
        this.state$.next({})
        this.loadSubscription = null
      },
    })
  }

  /**
   * Save current migration state to backend.
   * Uses a simple queue to handle rapid saves.
   */
  private saveState(): void {
    if (!this.accountId) return

    if (this.saveInProgress) {
      this.pendingSave = true
      return
    }

    this.saveInProgress = true

    this.uiStateService.save(this.accountId, this.state$.value, 'migration.accessMethod.').subscribe({
      next: () => this.handleSaveComplete(),
      error: () => this.handleSaveComplete(),
    })
  }

  /**
   * Handle save completion and process any pending saves.
   */
  private handleSaveComplete(): void {
    this.saveInProgress = false
    if (this.pendingSave) {
      this.pendingSave = false
      this.saveState()
    }
  }
}
